import type {MediaTypeLoadingStates, MediaTypePublishingParams, PostState, SocialMediaAccountId, SocialPost} from '@Components/social-media/post.vo';
import {PlatformMediaType} from '@Components/social-media/post.vo';
import {noop} from '@Utils/general.util';
import {useCallback, useEffect, useLayoutEffect, useRef, useState} from 'react';
import {useLatest} from 'ahooks';
import {runAutoMetricsRefreshBatch} from '@Libraries/social-media-metrics-refresh.library';
import {
  doPublishForAccount,
  getPostableSocialMediaAccountsForUser,
  getSocialMediaAccountsForUser,
  getSocialMediaPostsForUser,
  updateSocialMediaPostPublishingParams,
} from '@Components/social-media/social-media-thunk';
import {
  DEFAULT_POSTS_LIMIT,
  deleteBulkSocialAccounts,
  deleteSocialAccount,
  getAccountsInitialFilter,
  markAccountsReconnecting,
  markAccountsRetrying,
  markPostsAsRefreshing,
  resetPosts,
  unmarkAccountsReconnecting,
  unmarkAccountsRetrying,
  updateAccountSorter,
  updateDoRefetchGridForNewSorting,
  updatePostSorter,
} from '@Components/social-media/social-media-reducer';
import type {SocialAccount} from '@Components/social-media/account.vo';
import {AccountState, doesAccountNeedReconnection, getEffectiveAccountState} from '@Components/social-media/account.vo';
import type {
  AccountStateFilter,
  ContainerQueryParams,
  DesignSelection,
  PostStateFilter,
  PublishedUrlMapForAccount,
  RecentDesignForPublishing,
  ReconnectAccountOptions,
  RetryStatusForMediaType,
  SocialPlatform,
  SocialPostGridContainerQuery,
  UseDeleteSocialAccountResult,
  UseFixAccountAndRetryResult,
  UseReconnectAccountResult,
} from '@Components/social-media/social-media.types';
import {AccountSortingMetric, FixAndRetryPhase, PostSortingMetric, SocialPostEvent, socialGridContainerQuery} from '@Components/social-media/social-media.types';
import {removeConnectAccountBroadcastChannel, runReconnectWithBroadcast} from '@Libraries/social-media-account.library';
import type {ListItemStorage} from '@Components/list/list.types';
import {LIST_ITEM_TYPE} from '@Components/list/list.types';
import {isPostVisibleForFilter, refreshPostById} from '@Libraries/social-media-post.library';
import {getFailedPublishSlots, getPostFailedAccounts} from '@Libraries/social-media-error.library';
import {buildGridRequestParams, isGridScrolledToBottom, LAZY_LOAD_DEBOUNCE_MS, SOCIAL_POSTS_GRID_CONTAINER_ID} from '@Libraries/social-media-grid.library';
import {isPromotingEvent, trackEventPromotionScheduledForPlatform} from '@Libraries/social-media-event-promotion.library';
import useDidUpdateEffect from '@Hooks/useDidUpdateEffect';
import {executeThunk} from '@Utils/thunk.util';
import type {ErrorResponseUpdatePublishingParams} from '@Components/social-media-wizard/social-media-wizard.types';
import {SocialMediaWizardStep} from '@Components/social-media-wizard/social-media-wizard.types';
import type {SocialMediaPublishingParams} from '@Components/social-media/publish-params.types';
import {RetryStatus, SocialPostPublishingAction} from '@Components/social-media/publish-params.types';
import {LoadingStates} from '@Utils/loading.util';
import {
  removeAccountsFromWizard,
  initPublishingStatusMapForAccounts,
  unsetRetryingStatusForAccountWithMediaType,
  updateGraphicsReadiness,
  updatePublishErrorMessageForAccount,
  updatePublishingStatusForAccountWithMediaType,
  resetIncompletePublishingStatuses,
  updatePublishingStatusForAllAccountMediaTypes,
  updateSavingPublishingParamsState,
} from '@Components/social-media-wizard/social-media-wizard-reducer';
import {pollPublishGraphicsReadiness} from '@Components/social-media/social-media-graphics-readiness-thunk';
import {GA4EventName, GA4EventParamName, trackPublishingGA4Events} from '@Libraries/ga-events';
import {AccountResizeStatus} from '@Components/social-media-wizard/components/social-media-resize-options/social-media-resize-options.types';
import {useDesignsToPublish} from '@Components/social-media-wizard/social-media-wizard.hooks';
import type {StoryGuideline} from '@Components/social-media-wizard/components/social-media-publish-params-story-guideline';
import {useContainerQuery} from '@Hooks/useContainerQuery';
import {useAppDispatch, useAppSelector} from '@/hooks';
import {isUserLoggedIn, isUserPremium} from '@Libraries/user.library';
import {getPublishedSlotCounts, haveAllPublishingStatesLoaded, haveAllPublishingStatesSettled} from '@Libraries/social-media-wizard-accounts-library';
import type {PublishedSlotCounts} from '@Libraries/social-media-wizard-accounts-library';
import {
  buildScheduleTrackingEvents,
  getPlatformForAccount,
  handlePublishErrorResponse,
  handlePublishSuccessResponse,
  handleUpdatePublishingParamsError,
} from '@Libraries/social-media-publishing.library';
import {getSanitizedTimeZoneOffset} from '@Libraries/social-media-wizard-scheduling-library';
import {getSocialPlatformName} from '@Libraries/social-media-platform.library';
import {trackFakeDoorPostPublishedIfNeeded} from '@Libraries/marketing-planner.library';
import {openErrorModal} from '@Modals/error-modal';

export const useSocialMediaPosts = (): SocialPost[] => {
  return useAppSelector((state) => {
    return state.socialMediaGrid.posts;
  });
};

export const useSocialMediaHaveAccountsLoaded = (): boolean => {
  return useAppSelector((state) => {
    return state.socialMediaGrid.haveAccountsLoaded;
  });
};

export const useSocialMediaHavePostsLoaded = (): boolean => {
  return useAppSelector((state) => {
    return state.socialMediaGrid.havePostsLoaded;
  });
};

export const useDoRefetchGridForNewSorting = (): boolean => {
  return useAppSelector((state) => {
    return state.socialMediaGrid.doRefetchGridForNewSorting;
  });
};

export const useGetSharedInTeamPosts = (): boolean => {
  return useAppSelector((state) => {
    return state.socialMediaGrid.getSharedInTeamPosts;
  });
};

export const useSocialMediaPostsOffset = (): number => {
  return useAppSelector((state) => {
    return state.socialMediaGrid.posts.length;
  });
};

export const useAccountStateFilter = (): AccountStateFilter => {
  return useAppSelector((state) => {
    return state.socialMediaGrid.accountStateFilter;
  });
};

export const useIsAccountVisibleForFilter = (account: SocialAccount): boolean => {
  return useAppSelector((state) => {
    const {accountStateFilter, reconnectFilterArmed, armedReconnectBatchAccountIds} = state.socialMediaGrid;
    if (accountStateFilter[getEffectiveAccountState(account)]) {
      return true;
    }
    // Every account in the banner-armed batch stays put for the life of that batch, so a fixed row turns healthy in place instead of vanishing.
    return reconnectFilterArmed && armedReconnectBatchAccountIds[account.id];
  });
};

/** Pure predicate behind useShouldReleaseReconnectFilter, standalone (no useAppSelector) so it's unit-testable without a Redux store. The banner-armed filter releases once every account in that batch has been reconnected. */
export const shouldReleaseReconnectFilter = (isFilterArmed: boolean, armedBatchSize: number, doAnyBatchAccountsNeedReconnection: boolean): boolean => {
  return isFilterArmed && armedBatchSize > 0 && !doAnyBatchAccountsNeedReconnection;
};

export const useShouldReleaseReconnectFilter = (): boolean => {
  return useAppSelector((state) => {
    const {reconnectFilterArmed, armedReconnectBatchAccountIds, accounts} = state.socialMediaGrid;
    const armedBatchSize = Object.keys(armedReconnectBatchAccountIds).length;
    // Scoped to the batch: an account that breaks after the banner armed shouldn't keep this batch's filter stuck on.
    const doAnyBatchAccountsNeedReconnection = accounts.some((account) => armedReconnectBatchAccountIds[account.id] && doesAccountNeedReconnection(account));
    return shouldReleaseReconnectFilter(reconnectFilterArmed, armedBatchSize, doAnyBatchAccountsNeedReconnection);
  });
};

/** Pure predicate behind useIsAccountFilterActive, kept standalone (no useAppSelector) so it's unit-testable without a Redux store. */
export const isAccountStateFilterActive = (accountStateFilter: AccountStateFilter): boolean => {
  const initialFilter = getAccountsInitialFilter();
  return Object.values(AccountState).some((accountState) => accountStateFilter[accountState] !== initialFilter[accountState]);
};

/** True when accountStateFilter has been narrowed away from the all-visible default (e.g. by armReconnectAccountsFilter or the Status checkboxes), so the Status filter button can show an active indicator. */
export const useIsAccountFilterActive = (): boolean => {
  return useAppSelector((state) => isAccountStateFilterActive(state.socialMediaGrid.accountStateFilter));
};

export const useSocialPostGridContainerQuery = (): ContainerQueryParams => {
  const containerRef = useRef<HTMLDivElement>(null);
  const params = useContainerQuery(containerRef, socialGridContainerQuery);

  const containerQueryParamProps: SocialPostGridContainerQuery = {
    xsmall: params.xsmall,
    small: params.small,
    medium: params.medium,
    large: params.large,
    xlarge: params.xlarge,
    xxlarge: params.xxlarge,
  };

  return {
    containerQueryParamProps,
    containerRef,
    hasContainerQueryInitialized: params.small !== undefined,
  };
};

export const useIsPostVisibleForFilter = (postState: PostState): boolean => {
  const postStateFilter = useAppSelector((state) => {
    return state.socialMediaGrid.postStateFilter;
  });

  const [isVisible, setIsVisible] = useState(true);

  useDidUpdateEffect(() => {
    setIsVisible(isPostVisibleForFilter(postState, postStateFilter));
  }, [postStateFilter]);

  return isVisible;
};

export const usePostStateFilter = (): PostStateFilter => {
  return useAppSelector((state) => {
    return state.socialMediaGrid.postStateFilter;
  });
};

export const useSearchTerm = (): string => {
  return useAppSelector((state) => {
    return state.socialMediaGrid.searchTerm;
  });
};

export const usePostSorter = (): PostSortingMetric => {
  return useAppSelector((state) => {
    return state.socialMediaGrid.postSorter;
  });
};

export const useMorePostsOnServer = (): boolean => {
  return useAppSelector((state) => {
    return state.socialMediaGrid.morePostsOnServer;
  });
};

export const useGetDeletedPosts = (): boolean => {
  return useAppSelector((state) => {
    return state.socialMediaGrid.getDeletedPosts;
  });
};
export const useAccountSorter = (): AccountSortingMetric => {
  return useAppSelector((state) => {
    return state.socialMediaGrid.accountSorter;
  });
};

export const useSocialMediaAccounts = (): SocialAccount[] => {
  return useAppSelector((state) => {
    return state.socialMediaGrid.accounts;
  });
};

/** Single source of truth for how many connected accounts currently need reconnection — backs both the Accounts-tab count badge and the attention banner. */
export const useAccountsNeedingReconnectionCount = (): number => {
  return useAppSelector((state) => {
    return state.socialMediaGrid.accounts.filter(doesAccountNeedReconnection).length;
  });
};

export const useLoadSocialAccountsForWizard = (): void => {
  const dispatch = useAppDispatch();

  useEffect(() => {
    void executeThunk(() => {
      return dispatch(getPostableSocialMediaAccountsForUser());
    }).then(noop);
  }, []);
};

export const usePublishingStatusForAllAccounts = (): Record<SocialMediaAccountId, MediaTypeLoadingStates> => {
  return useAppSelector((state): Record<SocialMediaAccountId, MediaTypeLoadingStates> => {
    return state.socialMediaWizard.publishingStatusForMediaTypes;
  });
};

export const usePublishingStatusForAccountWithMediaType = (idSocialMediaAccount: SocialMediaAccountId, mediaType: PlatformMediaType): LoadingStates => {
  return useAppSelector((state) => {
    if (state.socialMediaWizard.publishingStatusForMediaTypes[idSocialMediaAccount] === undefined) {
      return LoadingStates.LOADING;
    }

    return state.socialMediaWizard.publishingStatusForMediaTypes[idSocialMediaAccount][mediaType];
  });
};

export const useRetryStatusForAccount = (idSocialMediaAccount: SocialMediaAccountId): RetryStatusForMediaType => {
  return useAppSelector((state): RetryStatusForMediaType => {
    return state.socialMediaWizard.retryPublishingMapForAccount[idSocialMediaAccount];
  });
};

export const useHaveAllPostsPublished = (): boolean => {
  const accountIdsToPublishTo = useAccountIdsToPublishTo();
  const publishingStatusForAllAccounts = usePublishingStatusForAllAccounts();
  const publishingParams = usePublishingParams();

  return haveAllPublishingStatesLoaded(accountIdsToPublishTo, publishingStatusForAllAccounts, publishingParams);
};

export const useHaveAllPostsSettled = (): boolean => {
  const accountIdsToPublishTo = useAccountIdsToPublishTo();
  const publishingStatusForAllAccounts = usePublishingStatusForAllAccounts();

  return haveAllPublishingStatesSettled(accountIdsToPublishTo, publishingStatusForAllAccounts);
};

export const usePublishedSlotCounts = (): PublishedSlotCounts => {
  const accountIdsToPublishTo = useAccountIdsToPublishTo();
  const publishingStatusForAllAccounts = usePublishingStatusForAllAccounts();
  const publishingParams = usePublishingParams();

  return getPublishedSlotCounts(accountIdsToPublishTo, publishingStatusForAllAccounts, publishingParams);
};

/**
 * Unified retry hook for all media types (POST, REELS, STORIES).
 * All hooks are called unconditionally at the top; guards run inside the effect.
 * The caller passes the exact platformMediaType it is rendering for, so the
 * hook's trigger is per-(account, mediaType) while retryStatusForAccount holds
 * the full Record<PlatformMediaType, RetryStatus | null> map.
 */
export const useRetryPublishingForAccount = (idSocialMediaAccount: SocialMediaAccountId, platformMediaType: PlatformMediaType): void => {
  const dispatch = useAppDispatch();
  const retryStatusForAccount = useRetryStatusForAccount(idSocialMediaAccount);
  const post = useSocialMediaWizardPost();
  const wizardAccounts = useSocialMediaWizardAccounts();

  useEffect(() => {
    if (!retryStatusForAccount) {
      console.error('No retry status for account with id ', idSocialMediaAccount, ' found');
      return;
    }

    const retryStatusForMediaType = retryStatusForAccount[platformMediaType];
    if (!retryStatusForMediaType || retryStatusForMediaType !== RetryStatus.RETRYING) {
      return;
    }

    const resetRetryStatusOnCompletion = (): void => {
      dispatch(unsetRetryingStatusForAccountWithMediaType({accountId: idSocialMediaAccount, platformMediaType}));
    };

    const onError = (): void => {
      handlePublishErrorResponse({dispatch, idSocialMediaAccount, platformMediaType, resetUrl: false});
      resetRetryStatusOnCompletion();
    };

    void executeThunk(
      () => {
        // The stale error message alone reads as FAILED, so it clears with the LOADING flip.
        dispatch(
          updatePublishErrorMessageForAccount({
            idSocialMediaAccount,
            publishErrorMessage: null,
            platformMediaType,
          })
        );

        dispatch(
          updatePublishingStatusForAccountWithMediaType({
            idSocialMediaAccount,
            publishingStatus: LoadingStates.LOADING,
            platformMediaType,
          })
        );

        return dispatch(
          doPublishForAccount({
            idPost: post ? post.id : null,
            idSocialMediaAccount,
            isRetry: true,
            mediaType: platformMediaType,
          })
        );
      },
      (data: object) => {
        handlePublishSuccessResponse({
          dispatch,
          idSocialMediaAccount,
          platformMediaType,
          data,
          platform: getPlatformForAccount(wizardAccounts, idSocialMediaAccount),
          onError,
        });
        resetRetryStatusOnCompletion();
      },
      () => {
        onError();
      }
    );
  }, [retryStatusForAccount]);
};

/**
 * Unifies Reel/Post and Story publishing: every hook is called unconditionally at the top, and the
 * media-type-specific guards (including the publishingParams existence check that applies only to
 * non-STORIES types) live inside the effect body.
 */
export const useStartPublishingForAccount = (idSocialMediaAccount: SocialMediaAccountId, platformMediaType: PlatformMediaType): void => {
  const dispatch = useAppDispatch();
  const savingPublishingParamsState = useSavingPublishingParamsState();
  const publishingAction = useSocialPostPublishingAction();
  const publishingParams = usePublishingParams();
  const publishingState = usePublishingStatusForAccountWithMediaType(idSocialMediaAccount, platformMediaType);
  const post = useSocialMediaWizardPost();
  const wizardAccounts = useSocialMediaWizardAccounts();

  useEffect(() => {
    if (publishingState === LoadingStates.LOADED || publishingState === LoadingStates.NOT_LOADED) {
      return;
    }

    if (savingPublishingParamsState !== LoadingStates.LOADED) {
      return;
    }

    if (publishingAction !== SocialPostPublishingAction.PUBLISH) {
      return;
    }

    // Existence guard preserved only for non-STORIES (matching the original reel/post hook); now runs inside the effect, not every render, per the rules-of-hooks fix.
    // Optional chaining is an intentional safety upgrade over the original's bare bracket access: a missing account slot now bails+logs instead of throwing (same outcome: no publish).
    if (platformMediaType !== PlatformMediaType.STORIES && !publishingParams[idSocialMediaAccount]?.[platformMediaType]) {
      console.error('No Publishing Params found for account with id: ', idSocialMediaAccount, ' and platform media type: ', platformMediaType);
      return;
    }

    const onError = (): void => {
      handlePublishErrorResponse({dispatch, idSocialMediaAccount, platformMediaType, resetUrl: true});
    };

    void executeThunk(
      () => {
        dispatch(
          updatePublishingStatusForAccountWithMediaType({
            idSocialMediaAccount,
            publishingStatus: LoadingStates.LOADING,
            platformMediaType,
          })
        );

        return dispatch(
          doPublishForAccount({
            idPost: post ? post.id : null,
            idSocialMediaAccount,
            mediaType: platformMediaType,
            isRetry: false,
          })
        );
      },
      (data: object) => {
        handlePublishSuccessResponse({
          dispatch,
          idSocialMediaAccount,
          platformMediaType,
          data,
          platform: getPlatformForAccount(wizardAccounts, idSocialMediaAccount),
          onError,
        });
      },
      () => {
        onError();
      }
    );
  }, [savingPublishingParamsState]);
};

export const useUpdatePublishingParams = (): void => {
  const dispatch = useAppDispatch();
  const post = useSocialMediaWizardPost();
  const publishingAction = useSocialPostPublishingAction();
  const step = useWizardStep();
  const designs = useDesignsToPublish();
  const publishingParams = usePublishingParams();
  const accountIdsToPublishTo = useAccountIdsToPublishTo();

  const updatePublishingStatusForSaveOrSchedule = (loadingState: LoadingStates): void => {
    if (publishingAction !== SocialPostPublishingAction.PUBLISH) {
      dispatch(updatePublishingStatusForAllAccountMediaTypes(loadingState));
    }
  };

  /**
   * Ordering guarantee for the publish fan-out: re-derives every account's statuses from the just-saved params
   * in the same tick savingPublishingParamsState flips to LOADED (the signal useStartPublishingForAccount fires
   * on), independent of PublishingPost child-mount order. The evidence-map rule in getInitialPublishStatusForMediaType
   * is what actually prevents a stale LOADED from skipping a slot; this makes that re-derive happen before the fire.
   */
  const armPublishingStatusesForPublish = (): void => {
    if (publishingAction === SocialPostPublishingAction.PUBLISH) {
      dispatch(initPublishingStatusMapForAccounts());
    }
  };

  const fireMultiPlatformPublishEvent = (): void => {
    if (!post || publishingAction !== SocialPostPublishingAction.PUBLISH) {
      return;
    }

    const numPlatforms = accountIdsToPublishTo.length;
    trackPublishingGA4Events(GA4EventName.SOCIALWIZARD_NUMPLATFORMS, {[GA4EventParamName.NUM_PLATFORMS]: `${numPlatforms}`});
  };

  const fireScheduleTrackingEvent = (): void => {
    const events = buildScheduleTrackingEvents(post, publishingParams, publishingAction);
    if (events.length === 0) {
      return;
    }

    trackFakeDoorPostPublishedIfNeeded();
    events.forEach(({platformName, isStory}) => {
      const platformForGA = isStory ? `${platformName}_${PlatformMediaType.STORIES}` : platformName;

      trackPublishingGA4Events(GA4EventName.POST_SCHEDULED, {[GA4EventParamName.PLATFORM]: platformForGA});

      if (isPromotingEvent()) {
        trackEventPromotionScheduledForPlatform(platformName);
      }
    });
  };

  const firePublishingParams = (): void => {
    updatePublishingStatusForSaveOrSchedule(LoadingStates.LOADING);
    dispatch(updateSavingPublishingParamsState(LoadingStates.LOADING));
    const publishOn: number | undefined = post ? post.publishOn : undefined;
    const timeZoneOffset: number | null = getSanitizedTimeZoneOffset(post);

    void executeThunk(
      () =>
        dispatch(
          updateSocialMediaPostPublishingParams({
            idPost: post ? post.id : null,
            designs: designs || null,
            publishOn: publishOn ?? null,
            timeZoneOffset,
            publishingParams,
          })
        ),
      () => {
        fireScheduleTrackingEvent();
        fireMultiPlatformPublishEvent();
        updatePublishingStatusForSaveOrSchedule(LoadingStates.LOADED);
        armPublishingStatusesForPublish();
        dispatch(updateSavingPublishingParamsState(LoadingStates.LOADED));
      },
      (response: ErrorResponseUpdatePublishingParams) => {
        const {error} = response;
        if (handleUpdatePublishingParamsError({error, dispatch, accountIdsToPublishTo})) {
          return;
        }

        fireMultiPlatformPublishEvent();
        // PUBLISH included: nothing will own its slots now, and LOADING would freeze the wizard.
        dispatch(resetIncompletePublishingStatuses());
        dispatch(updateSavingPublishingParamsState(LoadingStates.NOT_LOADED));
      }
    );
  };

  useEffect(() => {
    if (step !== SocialMediaWizardStep.PUBLISH) {
      return;
    }

    if (publishingAction !== SocialPostPublishingAction.PUBLISH) {
      firePublishingParams();
      return;
    }

    let cancelled = false;
    const idPost = post?.id;

    if (!idPost) {
      firePublishingParams();
      return;
    }

    const runWithReadinessGate = async (): Promise<void> => {
      // pollPublishGraphicsReadiness sets LOADING from its instant kickoff, and only when graphics are
      // actually still pending — so a ready design never shows the "preparing" flash.
      try {
        await dispatch(pollPublishGraphicsReadiness({idPost})).unwrap();
      } catch {
        // timeout or error — proceed anyway; backend gate + enqueue pre-render will recover
      }

      if (cancelled) {
        dispatch(updateGraphicsReadiness(LoadingStates.NOT_LOADED));
        return;
      }

      dispatch(updateGraphicsReadiness(LoadingStates.LOADED));
      firePublishingParams();
    };

    void runWithReadinessGate();

    return (): void => {
      cancelled = true;
    };
  }, [step]);
};

export const useSocialMediaWizardPost = (): SocialPost | undefined => {
  return useAppSelector((state) => {
    return state.socialMediaWizard.post;
  });
};

export const useSocialPostPublishingAction = (): SocialPostPublishingAction | undefined => {
  return useAppSelector((state) => {
    return state.socialMediaWizard.publishingAction;
  });
};

export const useSocialMediaWizardAccounts = (): SocialAccount[] => {
  return useAppSelector((state) => {
    return state.socialMediaWizard.accounts;
  });
};

export const useAccountsToPublishTo = (): SocialAccount[] => {
  const accounts = useSocialMediaWizardAccounts();
  const accountIdsToPublishTo = useAccountIdsToPublishTo();

  return accounts.filter((account) => accountIdsToPublishTo.includes(account.id));
};

export const useHasCommonCaption = (): boolean => {
  return useAppSelector((state) => {
    return state.socialMediaWizard.hasCommonCaption;
  });
};

export const useHasCommonPostCover = (): boolean => {
  return useAppSelector((state) => {
    return state.socialMediaWizard.hasCommonPostCover;
  });
};

export const useCommonCaption = (): string => {
  return useAppSelector((state) => {
    return state.socialMediaWizard.commonCaption;
  });
};

export const useTriggerNext = (): number => {
  return useAppSelector((state) => {
    return state.socialMediaWizard.triggerNext;
  });
};

export const useTriggerPrevious = (): number => {
  return useAppSelector((state) => {
    return state.socialMediaWizard.triggerPrevious;
  });
};

export const useAccountIdsToPublishTo = (): number[] => {
  return useAppSelector((state) => {
    return state.socialMediaWizard.accountIdsToPublishTo;
  });
};

export const usePostPublishingParamsForAccount = (socialMediaAccountId: SocialMediaAccountId): SocialMediaPublishingParams | null => {
  return useAppSelector((state) => {
    if (!state.socialMediaWizard.publishingParams[socialMediaAccountId]) {
      return null;
    }

    return state.socialMediaWizard.publishingParams[socialMediaAccountId].POST;
  });
};

export const useReelPublishingParamsForAccount = (socialMediaAccountId: SocialMediaAccountId): SocialMediaPublishingParams | null => {
  return useAppSelector((state) => {
    return state.socialMediaWizard.publishingParams[socialMediaAccountId]?.REELS ?? null;
  });
};

export const useStoryPublishingParamsForAccount = (socialMediaAccountId: SocialMediaAccountId): SocialMediaPublishingParams | null => {
  return useAppSelector((state) => {
    return state.socialMediaWizard.publishingParams[socialMediaAccountId]?.STORIES ?? null;
  });
};

export const useAllPublishingParamsForAccount = (socialMediaAccountId: SocialMediaAccountId): MediaTypePublishingParams => {
  return useAppSelector((state): MediaTypePublishingParams => {
    return state.socialMediaWizard.publishingParams[socialMediaAccountId];
  });
};

export const useStoryGuidelineForAccount = (accountId: number): StoryGuideline => {
  return useAppSelector((state): StoryGuideline => {
    return state.socialMediaWizard.storyGuidelineForAccount[accountId];
  });
};

export const usePublishingParams = (): Record<SocialMediaAccountId, MediaTypePublishingParams> => {
  return useAppSelector((state) => {
    return state.socialMediaWizard.publishingParams;
  });
};

export const useDesignToResize = (): DesignSelection | undefined => {
  return useAppSelector((state) => {
    return state.socialMediaWizard.designToResize;
  });
};

export const useAccountTypeToConnect = (): SocialPlatform | undefined => {
  return useAppSelector((state) => {
    return state.socialMediaWizard.accountTypeToConnect;
  });
};

export const useNextButtonIcon = (): string => {
  return useAppSelector((state) => {
    return state.socialMediaWizard.nextButtonIcon;
  });
};

export const useNextButtonText = (): string => {
  return useAppSelector((state) => {
    return state.socialMediaWizard.nextButtonText;
  });
};

export const useEmphasizedPlatform = (): SocialPlatform | undefined => {
  return useAppSelector((state) => {
    return state.socialMediaWizard.emphasizedPlatform;
  });
};

export const useNextButtonDisabled = (): boolean => {
  return useAppSelector((state) => {
    return state.socialMediaWizard.nextButtonDisabled;
  });
};

export const useSavingPublishingParamsState = (): LoadingStates => {
  return useAppSelector((state) => {
    return state.socialMediaWizard.savingPublishingParamsState;
  });
};

export const useWizardStep = (): SocialMediaWizardStep => {
  return useAppSelector((state) => {
    return state.socialMediaWizard.wizardStep;
  });
};

export const useLoadSocialAccounts = (): void => {
  const haveAccountsLoaded = useSocialMediaHaveAccountsLoaded();
  const dispatch = useAppDispatch();
  const isUserLogged = isUserLoggedIn();

  useEffect(() => {
    if (haveAccountsLoaded || !isUserLogged) {
      return;
    }

    void executeThunk(() => {
      return dispatch(getSocialMediaAccountsForUser());
    }).then(noop);
  }, [isUserLogged]);
};

export const useReloadGridForSorterIfNeeded = (): void => {
  const sortPostsBy = usePostSorter();
  const morePostsOnServer = useMorePostsOnServer();
  const numPostsLoaded = useSocialMediaPostsOffset();
  const getSharedInTeam = useGetSharedInTeamPosts();
  const dispatch = useAppDispatch();

  useDidUpdateEffect(() => {
    if (!morePostsOnServer) {
      return;
    }

    dispatch(updateDoRefetchGridForNewSorting(true));
    dispatch(resetPosts());

    const args = buildGridRequestParams({
      limit: numPostsLoaded,
      offset: 0,
      getDeleted: false,
      sortPostsBy,
      getSharedInTeam,
    });

    void executeThunk(() => {
      return dispatch(getSocialMediaPostsForUser(args));
    }).then();
  }, [sortPostsBy]);
};
export const useLoadSocialPosts = (getDeleted: boolean, getSharedInTeam: boolean): void => {
  const dispatch = useAppDispatch();
  const offset = useSocialMediaPostsOffset();
  const doRefetchGridForNewSorting = useDoRefetchGridForNewSorting();
  const havePostsLoaded = useSocialMediaHavePostsLoaded();
  const sortPostsBy = usePostSorter();
  const limit = DEFAULT_POSTS_LIMIT;

  const getPostsFromServer = async (): Promise<void> => {
    const args = buildGridRequestParams({limit, offset, getDeleted, sortPostsBy, getSharedInTeam});

    await executeThunk(() => {
      return dispatch(getSocialMediaPostsForUser(args));
    });
  };

  useEffect(() => {
    if (havePostsLoaded || doRefetchGridForNewSorting) {
      return;
    }

    void getPostsFromServer().then(noop);
  }, [havePostsLoaded]);
};

export const useLazyLoadPostsFromServer = (lazyloadFunction: VoidFunction): void => {
  const morePostsOnServer = useMorePostsOnServer();
  const havePostsLoaded = useSocialMediaHavePostsLoaded();
  const postStateFilter = usePostStateFilter();
  const numPostsLoaded = useSocialMediaPosts().length;

  useEffect(() => {
    window.addEventListener('scroll', doLazyloadIfNeeded, {passive: true});

    return (): void => {
      window.removeEventListener('scroll', doLazyloadIfNeeded);
    };
  }, [morePostsOnServer]);

  const doLazyloadIfNeeded = window.PMW.debounce(() => {
    if (!morePostsOnServer) {
      return;
    }

    if (isGridScrolledToBottom(SOCIAL_POSTS_GRID_CONTAINER_ID)) {
      lazyloadFunction();
    }
  }, LAZY_LOAD_DEBOUNCE_MS) as VoidFunction;

  // A sparse client-side status filter can collapse the grid below the viewport, so the scroll event never fires — keep paging while the grid is too short to scroll and the server still has posts.
  useEffect(() => {
    if (!havePostsLoaded || !morePostsOnServer) {
      return;
    }

    if (isGridScrolledToBottom(SOCIAL_POSTS_GRID_CONTAINER_ID)) {
      lazyloadFunction();
    }
  }, [havePostsLoaded, morePostsOnServer, postStateFilter, numPostsLoaded]);
};

export const useIsPostRefreshing = (postId: string): boolean => {
  return useAppSelector((state) => state.socialMediaGrid.refreshingPostIds[postId] ?? false);
};

/** Sentinel account id for useIsAccountReconnecting when there is no account to track, so the hook is still called unconditionally on every render. */
export const NO_ACCOUNT_ID = -1;

export const useIsAccountReconnecting = (accountId: number): boolean => {
  return useAppSelector((state) => state.socialMediaGrid.reconnectingAccountIds[accountId] ?? false);
};

/** True while any account is mid-reconnect. Drives the one-at-a-time gate on other accounts' "Fix this" buttons — the reconnect BroadcastChannel is per-user with no account id, so two concurrent reconnects cross-talk. */
export const useIsAnyAccountReconnecting = (): boolean => {
  return useAppSelector((state) => Object.keys(state.socialMediaGrid.reconnectingAccountIds).length > 0);
};

export const useIsAccountRetrying = (accountId: number): boolean => {
  return useAppSelector((state) => state.socialMediaGrid.retryingAccountIds[accountId] ?? false);
};

/** True while any account's failed slots are being retried. Gates other accounts' "Fix this" so concurrent per-account retries can't race the publish-claim lock on one post. */
export const useIsAnyAccountRetrying = (): boolean => {
  return useAppSelector((state) => Object.keys(state.socialMediaGrid.retryingAccountIds).length > 0);
};

/** Reassurance floor for the reconnect skeleton: it stays up for at least this long even if the accounts refetch settles sooner. */
export const RECONNECT_SKELETON_MIN_MS = 2000;

const waitForRemainingSkeletonWindow = async (startedAt: number): Promise<void> => {
  const elapsed = performance.now() - startedAt;
  if (elapsed >= RECONNECT_SKELETON_MIN_MS) {
    return;
  }

  await new Promise<void>((resolve): void => {
    setTimeout(resolve, RECONNECT_SKELETON_MIN_MS - elapsed);
  });
};

/**
 * The single reconnect handshake, reused by the wizard account list, My Accounts grid, dialog
 * error rows, and grid inline Fix-this. Marks the account reconnecting, drives the
 * reAuthenticateInModal OAuth popup + broadcast-channel handshake, and on success refetches
 * accounts before clearing the reconnecting flag (never sooner than RECONNECT_SKELETON_MIN_MS).
 * Render-time skeleton state comes from useIsAccountReconnecting(id), not from this hook's result.
 * Channels are tracked per account id so starting a reconnect for one account never strands
 * an in-flight reconnect for another (see channelsByAccountIdRef below).
 */
export const useReconnectAccount = (): UseReconnectAccountResult => {
  const dispatch = useAppDispatch();
  const channelsByAccountIdRef = useRef<Map<number, BroadcastChannel>>(new Map());

  const startReconnect = useCallback(
    (account: SocialAccount, opts?: ReconnectAccountOptions): void => {
      const startedAt = performance.now();
      dispatch(markAccountsReconnecting([account.id]));

      const clearChannelForAccount = (): void => {
        removeConnectAccountBroadcastChannel(channelsByAccountIdRef.current.get(account.id));
        channelsByAccountIdRef.current.delete(account.id);
      };

      // Channel is per-user, not per-account, and carries no account id — another account's in-flight reconnect can also trigger these callbacks (self-corrects on its own next refetch).
      const abortReconnecting = (): void => {
        clearChannelForAccount();
        dispatch(unmarkAccountsReconnecting([account.id]));
        opts?.onFailed?.();
      };

      const finishReconnecting = (): void => {
        clearChannelForAccount();
        void (async (): Promise<void> => {
          await dispatch(getSocialMediaAccountsForUser());
          await waitForRemainingSkeletonWindow(startedAt);
          dispatch(unmarkAccountsReconnecting([account.id]));
          opts?.onReconnected?.(account.id);
        })();
      };

      const newChannel = runReconnectWithBroadcast({
        idAccount: account.id,
        accountType: account.type,
        previousChannel: channelsByAccountIdRef.current.get(account.id),
        onSuccess: finishReconnecting,
        onNothingHappened: abortReconnecting,
        onFailure: abortReconnecting,
      });

      channelsByAccountIdRef.current.delete(account.id);
      if (newChannel) {
        channelsByAccountIdRef.current.set(account.id, newChannel);
      }
    },
    [dispatch]
  );

  useEffect(() => {
    const channelsByAccountId = channelsByAccountIdRef.current;

    return (): void => {
      channelsByAccountId.forEach((channel) => {
        removeConnectAccountBroadcastChannel(channel);
      });
      channelsByAccountId.clear();
    };
  }, []);

  return {startReconnect};
};

export const useDeleteSocialAccount = (): UseDeleteSocialAccountResult => {
  const dispatch = useAppDispatch();

  const deleteAccount = useCallback(
    async (account: SocialAccount): Promise<void> => {
      try {
        const response = (await window.PMW.writeLocal('socialmedia/deleteAccountForUser', {
          id: account.id,
        })) as string;
        const deletedIds = JSON.parse(response) as number[];

        dispatch(deleteSocialAccount(account.id));
        if (deletedIds.length > 0) {
          dispatch(deleteBulkSocialAccounts(deletedIds));
        }
        dispatch(removeAccountsFromWizard([account.id, ...deletedIds]));
      } catch {
        openErrorModal({
          message: window.i18next.t('pmwjs_social_account_delete_error'),
        });
      } finally {
        window.dispatchEvent(new Event(SocialPostEvent.RESET_GRID));
        trackPublishingGA4Events(GA4EventName.ACCOUNT_DELETED, {[GA4EventParamName.PLATFORM]: getSocialPlatformName(account.type)});
      }
    },
    [dispatch]
  );

  return {deleteAccount};
};

const hasSlotPublishFailed = (result: unknown): boolean => {
  if (typeof result !== 'object' || result === null || !('publishErrorMessage' in result)) {
    return false;
  }

  return typeof result.publishErrorMessage === 'string' && result.publishErrorMessage !== '';
};

/**
 * The atomic "Fix this" unit reused by the dialog error rows, grid inline Fix-this, and the
 * guided modal: reconnects the account if needed, retries each of its failed publish slots
 * one at a time (concurrent per-slot publishes on one post race on the publish claim lock),
 * then refreshes the post so the grid and dialog re-render. Retry is per-account by design —
 * callers loop accounts one at a time rather than firing this hook's fixAndRetry concurrently.
 * getPhase(accountId) exposes IDLE/RECONNECTING/RETRYING/DONE/FAILED for buttons/rows to render.
 */
export const useFixAccountAndRetry = (
  onReconnected?: (accountId: number) => void,
  onFixFailed?: (accountId: number) => void,
  onFixSucceeded?: (accountId: number) => void
): UseFixAccountAndRetryResult => {
  const dispatch = useAppDispatch();
  const {startReconnect} = useReconnectAccount();
  const [phases, setPhases] = useState<Record<number, FixAndRetryPhase>>({});

  const setPhase = useCallback((accountId: number, phase: FixAndRetryPhase): void => {
    setPhases((prev) => ({...prev, [accountId]: phase}));
  }, []);

  const getPhase = useCallback((accountId: number): FixAndRetryPhase => phases[accountId] ?? FixAndRetryPhase.IDLE, [phases]);

  const reconnectIfNeeded = useCallback(
    (account: SocialAccount): Promise<void> => {
      if (!doesAccountNeedReconnection(account)) {
        return Promise.resolve();
      }

      return new Promise<void>((resolve, reject) => {
        setPhase(account.id, FixAndRetryPhase.RECONNECTING);
        startReconnect(account, {
          onReconnected: () => {
            resolve();
          },
          onFailed: () => {
            reject(new Error('Account reconnect failed or was cancelled'));
          },
        });
      });
    },
    [setPhase, startReconnect]
  );

  const retryFailedSlots = useCallback(
    async (account: SocialAccount, post: SocialPost): Promise<void> => {
      setPhase(account.id, FixAndRetryPhase.RETRYING);
      dispatch(markAccountsRetrying([account.id]));
      try {
        const failedSlots = getFailedPublishSlots(post).filter((slot) => slot.account.id === account.id);

        for (const slot of failedSlots) {
          // eslint-disable-next-line no-await-in-loop -- sequential per-slot retry avoids publish-claim races on one post
          const result: unknown = await dispatch(doPublishForAccount({idPost: post.id, idSocialMediaAccount: account.id, mediaType: slot.mediaType, isRetry: true})).unwrap();

          if (hasSlotPublishFailed(result)) {
            throw new Error('Publish retry failed for slot');
          }
        }
      } finally {
        dispatch(unmarkAccountsRetrying([account.id]));
      }
    },
    [dispatch, setPhase]
  );

  const fixAndRetry = useCallback(
    async (account: SocialAccount, post: SocialPost): Promise<void> => {
      // Fire the reconnect/fail callbacks from here (not a per-row phase effect) so they survive the row
      // re-rendering or unmounting during the seconds-long reconnect — this promise runs to completion regardless.
      const neededReconnection = doesAccountNeedReconnection(account);
      trackPublishingGA4Events(GA4EventName.MYSTUFF_SOCIALPOSTS_FIXTHIS);

      try {
        await reconnectIfNeeded(account);
        if (neededReconnection) {
          onReconnected?.(account.id);
        }
        await retryFailedSlots(account, post);
        await refreshPostById(post.id);
        setPhase(account.id, FixAndRetryPhase.DONE);
        onFixSucceeded?.(account.id);
      } catch {
        setPhase(account.id, FixAndRetryPhase.FAILED);
        onFixFailed?.(account.id);
      }
    },
    [reconnectIfNeeded, retryFailedSlots, setPhase, onReconnected, onFixFailed, onFixSucceeded]
  );

  return {fixAndRetry, getPhase};
};

export const useIsAttentionBannerDismissed = (): boolean => {
  return useAppSelector((state) => state.socialMediaGrid.isAttentionBannerDismissed);
};

/**
 * Auto-refreshes metrics for the grid's stale published posts. Each ID is kicked
 * off at most once per mount (lazy-loading refreshes only the new posts) via one
 * kickoff + one long-poll loop over the whole batch, cancelled on unmount. Each
 * post clears its skeleton the moment it settles — nothing ready is held back.
 * Premium-only.
 *
 * `refreshablePostIds` arrive in the grid's render order, so the backend enqueues
 * the stale posts top-first (a best-effort nudge toward above-the-fold settling
 * sooner). They're the posts the grid already determined are stale, so we
 * optimistically show their skeleton BEFORE paint (a layout effect) — the grid
 * never flashes stale numbers and then a skeleton. After the kickoff, posts the
 * server didn't enqueue revert to their numbers.
 */
export const useAutoRefreshMetricsForVisiblePosts = (refreshablePostIds: string[]): void => {
  const dispatch = useAppDispatch();
  const posts = useSocialMediaPosts();
  const postsRef = useLatest(posts);
  const dialogPostId = useAppSelector((state) => state.socialPostDialog.post?.id ?? null);
  const dialogPostIdRef = useLatest(dialogPostId);
  const kickedOffRef = useRef<Set<string>>(new Set());
  const activeEventIdsRef = useRef<Set<string>>(new Set());
  const eventIdCounterRef = useRef(0);
  const abortedRef = useRef(false);
  const havePostsLoaded = useSocialMediaHavePostsLoaded();
  const prevHavePostsLoadedRef = useRef(havePostsLoaded);

  useEffect(() => {
    // Full-grid reset (sort/team/trash toggle) empties posts and flips havePostsLoaded false: forget kicked-off IDs so the reloaded page is re-evaluated. Lazy-load pagination also flips havePostsLoaded false but keeps the loaded posts, so the empty-posts guard skips it — already-kicked-off rows aren't re-kicked. Backend guard still dedupes within the freshness window.
    if (prevHavePostsLoadedRef.current && !havePostsLoaded && postsRef.current.length === 0) {
      kickedOffRef.current.clear();
    }
    prevHavePostsLoadedRef.current = havePostsLoaded;
  }, [havePostsLoaded]);

  useLayoutEffect(() => {
    if (!isUserPremium()) {
      return;
    }

    const newPostIds = refreshablePostIds.filter((id) => !kickedOffRef.current.has(id));
    if (newPostIds.length > 0) {
      dispatch(markPostsAsRefreshing(newPostIds));
    }
  }, [refreshablePostIds]);

  useEffect(() => {
    if (!isUserPremium()) {
      return;
    }

    const newPostIds = refreshablePostIds.filter((id) => !kickedOffRef.current.has(id));
    if (newPostIds.length === 0) {
      return;
    }

    newPostIds.forEach((id) => kickedOffRef.current.add(id));
    eventIdCounterRef.current += 1;
    const eventId = `social-metrics-refresh-${eventIdCounterRef.current}`;
    activeEventIdsRef.current.add(eventId);

    void runAutoMetricsRefreshBatch(newPostIds, eventId, dispatch, postsRef, dialogPostIdRef, abortedRef).finally(() => {
      activeEventIdsRef.current.delete(eventId);
    });
  }, [refreshablePostIds]);

  useEffect(() => {
    return (): void => {
      abortedRef.current = true;
      activeEventIdsRef.current.forEach((eventId) => {
        document.dispatchEvent(new Event(window.PMW.getPollingStopEvent(eventId)));
      });
    };
  }, []);
};

export const useAccountsSorterListItems = (): ListItemStorage[] => {
  const dispatch = useAppDispatch();

  const accountSorter = useAccountSorter();
  return [
    {
      id: AccountSortingMetric.DATE_CREATED,
      isSelected: accountSorter === AccountSortingMetric.DATE_CREATED,
      type: LIST_ITEM_TYPE.DEFAULT_3,
      text: window.i18next.t('pmwjs_date_added'),
      onClick(_listId?: string, listItemId?: string): void {
        if (listItemId === (AccountSortingMetric.DATE_CREATED as string)) {
          dispatch(updateAccountSorter(AccountSortingMetric.DATE_CREATED));
        }
      },
    },
    {
      id: AccountSortingMetric.BY_PLATFORM,
      isSelected: accountSorter === AccountSortingMetric.BY_PLATFORM,
      type: LIST_ITEM_TYPE.DEFAULT_3,
      text: window.i18next.t('pmwjs_platform'),
      onClick(_listId?: string, listItemId?: string): void {
        if (listItemId === (AccountSortingMetric.BY_PLATFORM as string)) {
          dispatch(updateAccountSorter(AccountSortingMetric.BY_PLATFORM));
        }
      },
    },
  ];
};
export const usePostsSorterListItems = (): ListItemStorage[] => {
  const dispatch = useAppDispatch();

  const postSorter = usePostSorter();
  return [
    {
      id: PostSortingMetric.LAST_MODIFIED,
      isSelected: postSorter === PostSortingMetric.LAST_MODIFIED,
      type: LIST_ITEM_TYPE.DEFAULT_3,
      text: window.i18next.t('pmwjs_last_modified'),
      onClick(_listId?: string, listItemId?: string): void {
        if (listItemId === (PostSortingMetric.LAST_MODIFIED as string)) {
          dispatch(updatePostSorter(PostSortingMetric.LAST_MODIFIED));
        }
      },
    },
    {
      id: PostSortingMetric.DATE_PUBLISHED,
      isSelected: postSorter === PostSortingMetric.DATE_PUBLISHED,
      type: LIST_ITEM_TYPE.DEFAULT_3,
      text: window.i18next.t('pmwjs_publishing_date'),
      onClick(_listId?: string, listItemId?: string): void {
        if (listItemId === (PostSortingMetric.DATE_PUBLISHED as string)) {
          dispatch(updatePostSorter(PostSortingMetric.DATE_PUBLISHED));
        }
      },
    },
  ];
};

export const useAccountResizeCompletedStatusForPosters = (account: SocialAccount | undefined, platformMediaType: PlatformMediaType): AccountResizeStatus => {
  return useAppSelector((state): AccountResizeStatus => {
    if (!account) {
      return AccountResizeStatus.NOT_RESIZED;
    }

    const resizingStatusForDesigns = state.socialMediaWizard.accountResizeStatusMapForMediaType[account.id][platformMediaType];

    if (!resizingStatusForDesigns) {
      return AccountResizeStatus.NOT_RESIZED;
    }

    for (const resizePageMap of Object.values(resizingStatusForDesigns)) {
      for (const resizeInformation of Object.values(resizePageMap)) {
        if (resizeInformation !== AccountResizeStatus.RESIZE_DONE) {
          return resizeInformation;
        }
      }
    }

    return AccountResizeStatus.RESIZE_DONE;
  });
};

export const usePublishedAccountUrlMap = (): PublishedUrlMapForAccount => {
  return useAppSelector((state): PublishedUrlMapForAccount => {
    return state.socialMediaWizard.publishedAccountUrlMapForMediaType;
  });
};

export const useHasAccounts = (): boolean => {
  return useAppSelector((state) => {
    return state.socialMediaGrid.accounts.length > 0;
  });
};

export const useRecentDesignsForPublishing = (): RecentDesignForPublishing[] => {
  return useAppSelector((state) => {
    return state.socialMediaGrid.recentDesignsForPublishing;
  });
};

export const useHaveRecentDesignsForPublishingLoaded = (): boolean => {
  return useAppSelector((state) => {
    return state.socialMediaGrid.haveRecentDesignsForPublishingLoaded;
  });
};

/**
 * Tracks a post as its failed accounts get fixed. Listens for SocialPostEvent.UPDATED and, when the
 * post's failed-account count drops, fires onProgress (some failures remain) or onResolved (the last
 * one was fixed). Returns the latest post so a surface renders from one source instead of each
 * re-implementing the listener + previous-count ref + setPost. Pass stable (useCallback) callbacks.
 */
export const usePostFixProgress = (socialPost: SocialPost, callbacks: {onProgress?: () => void; onResolved?: () => void}): SocialPost => {
  const [post, setPost] = useState<SocialPost>(socialPost);
  const previousFailedCountRef = useRef<number>(getPostFailedAccounts(socialPost).length);
  const {onProgress, onResolved} = callbacks;

  useEffect(() => {
    const onSocialPostUpdated = (event: Event): void => {
      const customEvent = event as CustomEvent<{postId: string; post: SocialPost}>;
      if (customEvent.detail.postId !== post.id) {
        return;
      }

      const updatedPost = customEvent.detail.post;
      const newFailedCount = getPostFailedAccounts(updatedPost).length;

      if (newFailedCount < previousFailedCountRef.current) {
        if (newFailedCount === 0) {
          onResolved?.();
        } else {
          onProgress?.();
        }
      }

      previousFailedCountRef.current = newFailedCount;
      setPost(updatedPost);
    };

    window.addEventListener(SocialPostEvent.UPDATED, onSocialPostUpdated);
    return (): void => {
      window.removeEventListener(SocialPostEvent.UPDATED, onSocialPostUpdated);
    };
  }, [post.id, onProgress, onResolved]);

  return post;
};
